import React, { useState, useEffect } from 'react'
import { security, USER_ROLES } from '../config/security'
import './MonteSeuKit.css'

const MonteSeuKit = ({ onBack, userRole }) => {
  // Estados para os dados
  const [facets, setFacets] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [canAssembleKit, setCanAssembleKit] = useState(false)
  
  // Estados para o carrinho
  const [cartData, setCartData] = useState(null)
  const [loadingCart, setLoadingCart] = useState(false)
  const [showCart, setShowCart] = useState(false)
  
  // Estados para os filtros
  const [filters, setFilters] = useState({
    region: 'PA',
    inverter_type: 'String',
    inverter_manufacturer: '',
    inverter_nominal_power: '',
    structure_installation: '',
    network_type: '',
    partner_id: null
  })
  
  // Estados para autenticação automática
  const [token, setToken] = useState('')
  const [tokenExpiry, setTokenExpiry] = useState(null)

  // Carregar token salvo quando o componente montar
  useEffect(() => {
    loadSavedToken()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Carregar dados quando o componente montar ou filtros mudarem
  useEffect(() => {
    fetchKitData()
  }, [filters, token]) // eslint-disable-line react-hooks/exhaustive-deps

  // Verificar permissão de acesso
  if (!security.canAccessMonteSeuKit(userRole)) {
    return (
      <div className="monte-kit-container">
        <div className="monte-kit-header">
          <h1 className="monte-kit-title">🔧 Monte seu Kit</h1>
          <button onClick={onBack} className="back-button">
            ← Voltar ao Dashboard
          </button>
        </div>
        <div className="access-denied">
          <h2>🚫 Acesso Negado</h2>
          <p>Você não tem permissão para acessar esta funcionalidade.</p>
          <p>Apenas administradores podem acessar o Monte seu Kit.</p>
        </div>
      </div>
    )
  }
  
  // Credenciais fixas
  const credentials = {
    username: 'tecsol.ananindeua@gmail.com',
    password: 'JUwa@2024' // Substitua pela senha real
  }

  // Função para obter token de acesso
  const getAccessToken = async () => {
    try {
      const formData = new URLSearchParams()
      formData.append('grant_type', 'password')
      formData.append('client_id', 'ecommerce')
      formData.append('username', credentials.username)
      formData.append('password', credentials.password)

      const response = await fetch('https://sso.solfacil.com.br/realms/General/protocol/openid-connect/token', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded'
        },
        body: formData
      })

      if (!response.ok) {
        throw new Error(`Erro na autenticação: ${response.status}`)
      }

      const data = await response.json()
      
      if (data.error) {
        throw new Error(data.error_description || data.error)
      }

      // Calcular quando o token expira
      const expiresAt = new Date(Date.now() + (data.expires_in * 1000))
      
      // Salvar token e informações no localStorage
      localStorage.setItem('solfacil_token', data.access_token)
      localStorage.setItem('solfacil_token_expiry', expiresAt.toISOString())
      localStorage.setItem('solfacil_credentials', JSON.stringify(credentials))

      setToken(data.access_token)
      setTokenExpiry(expiresAt)
      
      console.log('Token obtido com sucesso. Expira em:', expiresAt.toLocaleString('pt-BR'))
      
      return data.access_token
    } catch (error) {
      console.error('Erro ao obter token:', error)
      throw error
    }
  }

  // Função para verificar se o token está válido
  const isTokenValid = () => {
    if (!token || !tokenExpiry) return false
    const now = new Date()
    // Renovar token 5 minutos antes de expirar
    const renewTime = new Date(tokenExpiry.getTime() - (5 * 60 * 1000))
    return now < renewTime
  }

  // Função para carregar token salvo
  const loadSavedToken = () => {
    try {
      const savedToken = localStorage.getItem('solfacil_token')
      const savedExpiry = localStorage.getItem('solfacil_token_expiry')
      const savedCredentials = localStorage.getItem('solfacil_credentials')

      if (savedToken && savedExpiry && savedCredentials) {
        const expiry = new Date(savedExpiry)
        const now = new Date()
        
        // Se ainda não expirou (com margem de 5 minutos)
        if (now < new Date(expiry.getTime() - (5 * 60 * 1000))) {
          setToken(savedToken)
          setTokenExpiry(expiry)
          console.log('Token carregado do cache. Expira em:', expiry.toLocaleString('pt-BR'))
          return true
        } else {
          console.log('Token expirado, será renovado')
          // Limpar token expirado
          localStorage.removeItem('solfacil_token')
          localStorage.removeItem('solfacil_token_expiry')
        }
      }
    } catch (error) {
      console.error('Erro ao carregar token salvo:', error)
    }
    return false
  }

  // Função para buscar dados dos filtros
  const fetchKitData = async () => {
    setLoading(true)
    setError(null)
    
    try {
      // Verificar se precisa obter/renovar token
      let currentToken = token
      if (!isTokenValid()) {
        currentToken = await getAccessToken()
      }

      const query = `
        query searchProductsAssembleKit($region: String!, $inverter_manufacturer: String, $inverter_nominal_power: String, $structure_installation: String, $network_type: String, $partner_id: Int, $inverter_type: String) {
          searchProductsAssembleKit(
            region: $region
            inverter_manufacturer: $inverter_manufacturer
            inverter_nominal_power: $inverter_nominal_power
            structure_installation: $structure_installation
            network_type: $network_type
            partner_id: $partner_id
            inverter_type: $inverter_type
          ) {
            canAssembleKit
            dc_id
            facets {
              filter_name
              title
              options {
                from
                to
                count
                value
                __typename
              }
              __typename
            }
            __typename
          }
        }
      `

      const variables = {
        region: filters.region,
        inverter_type: filters.inverter_type,
        inverter_manufacturer: filters.inverter_manufacturer || null,
        inverter_nominal_power: filters.inverter_nominal_power || null,
        structure_installation: filters.structure_installation || null,
        network_type: filters.network_type || null,
        partner_id: filters.partner_id
      }

      const response = await fetch('https://kong.solfacil.com.br/prd-bff-store/api/graphql', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${currentToken}`,
        },
        body: JSON.stringify({
          operationName: 'searchProductsAssembleKit',
          query,
          variables
        })
      })

      if (!response.ok) {
        throw new Error(`Erro na requisição: ${response.status}`)
      }

      const data = await response.json()
      
      if (data.errors) {
        throw new Error(data.errors[0].message)
      }

      // Debug: verificar estrutura dos dados recebidos
      console.log('Resposta da API Monte Kit:', data)
      console.log('Facets recebidos:', data.data.searchProductsAssembleKit.facets)
      
      setFacets(data.data.searchProductsAssembleKit.facets || [])
      setCanAssembleKit(data.data.searchProductsAssembleKit.canAssembleKit || false)
      
    } catch (err) {
      console.error('Erro ao buscar dados do kit:', err)
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  // Função para buscar carrinho personalizado
  const fetchCustomCart = async () => {
    setLoadingCart(true)
    setError(null)
    
    try {
      // Verificar se precisa obter/renovar token
      let currentToken = token
      if (!isTokenValid()) {
        currentToken = await getAccessToken()
      }

      const query = `
        query getCustomCart($channel: String!, $segmentation_id: String, $partner_id: Int) {
          getCustomCart(
            channel: $channel
            segmentation_id: $segmentation_id
            partner_id: $partner_id
          ) {
            id
            assembled_kit
            reference_description
            dc_id
            region
            items {
              amount
              category
              description
              datasheet
              images {
                url
                __typename
              }
              info {
                title
                value
                __typename
              }
              discount_rate
              price
              price_raw
              price_from
              sku
              status {
                description
                id
                __typename
              }
              available_at
              pre_sale
              total
              subtotal
              __typename
            }
            shipping {
              address {
                city
                complement
                district
                number
                state
                street
                zipcode
                __typename
              }
              recipient {
                email
                name
                phone
                __typename
              }
              type
              delivery_rural_area
              __typename
            }
            payment {
              buyer {
                type
                phone
                email
                document_number
                name
                __typename
              }
              method {
                id
                name
                __typename
              }
              __typename
            }
            summary {
              body {
                items {
                  title
                  type
                  information
                  value
                  __typename
                }
                __typename
              }
              __typename
            }
            status {
              description
              id
              __typename
            }
            type {
              id
              description
              __typename
            }
            proposal {
              code
              __typename
            }
            big_numbers {
              name
              label
              value
              __typename
            }
            assembled_kit
            pre_sale
            available_at
            subtotal
            total
            product_total
            partner {
              id
              solfacil_plus {
                discount_rate
                level
                __typename
              }
              __typename
            }
            coupon {
              id
              name
              discount {
                type
                amount
                result
                __typename
              }
              __typename
            }
            __typename
          }
        }
      `

      const variables = {
        channel: 'autoservico',
        segmentation_id: null,
        partner_id: null
      }

      const response = await fetch('https://kong.solfacil.com.br/prd-bff-store/api/graphql', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${currentToken}`,
        },
        body: JSON.stringify({
          operationName: 'getCustomCart',
          query,
          variables
        })
      })

      if (!response.ok) {
        throw new Error(`Erro na requisição: ${response.status}`)
      }

      const data = await response.json()
      
      if (data.errors) {
        throw new Error(data.errors[0].message)
      }

      // Debug: verificar estrutura dos dados recebidos
      console.log('Resposta do Carrinho:', data)
      console.log('Dados do Carrinho:', data.data.getCustomCart)
      
      setCartData(data.data.getCustomCart)
      setShowCart(true)
      
    } catch (err) {
      console.error('Erro ao buscar carrinho:', err)
      setError(err.message)
    } finally {
      setLoadingCart(false)
    }
  }

  // Handlers para mudanças nos filtros
  const handleFilterChange = (filterName, value) => {
    setFilters(prev => ({ ...prev, [filterName]: value }))
  }

  // Função para formatar potência com vírgula como separador decimal
  const formatPotencia = (value) => {
    if (!value) return ''
    
    // Remove caracteres não numéricos exceto vírgula e ponto
    let cleanValue = value.replace(/[^\d,.]/g, '')
    
    // Substitui ponto por vírgula se houver
    cleanValue = cleanValue.replace('.', ',')
    
    // Limita a 2 casas decimais após a vírgula
    const parts = cleanValue.split(',')
    if (parts.length > 1) {
      parts[1] = parts[1].substring(0, 2)
      cleanValue = parts.join(',')
    }
    
    // Adiciona "kW" no final se houver valor
    return cleanValue ? `${cleanValue} kW` : ''
  }

  // Função para processar entrada do usuário e adicionar kW automaticamente
  const handlePotenciaInput = (inputValue) => {
    // Remove "kW" se já existir para evitar duplicação
    let cleanInput = inputValue.replace(/\s*kW\s*/g, '')
    
    // Se o usuário digitou apenas números (sem vírgula), adiciona kW automaticamente
    if (/^\d+$/.test(cleanInput)) {
      const formattedValue = formatPotencia(cleanInput)
      const numericValue = convertPotenciaToNumber(cleanInput)
      handleFilterChange('inverter_nominal_power', numericValue)
      return formattedValue
    }
    
    // Para outros casos (com vírgula), formata normalmente
    const formattedValue = formatPotencia(cleanInput)
    const numericValue = convertPotenciaToNumber(cleanInput)
    handleFilterChange('inverter_nominal_power', numericValue)
    return formattedValue
  }

  // Função para converter vírgula para ponto (para envio à API)
  const convertPotenciaToNumber = (value) => {
    if (!value) return ''
    return value.replace(',', '.')
  }

  // Função para obter opções de um facet específico
  const getFacetOptions = (filterName) => {
    const facet = facets.find(f => f.filter_name === filterName)
    return facet ? facet.options : []
  }


  return (
    <div className="monte-kit-container">
      <div className="monte-kit-header">
        <h1 className="monte-kit-title">🔧 Monte seu Kit</h1>
        <button onClick={onBack} className="back-button">
          ← Voltar ao Dashboard
        </button>
      </div>

      {/* Status do Kit */}
      {canAssembleKit && (
        <div className="kit-status-success">
          ✅ Kit pode ser montado com os critérios selecionados
        </div>
      )}

      {/* Formulário de Configuração */}
      <div className="kit-form-container">
        <h3 className="form-title">Configuração do Kit</h3>
        
        <div className="form-grid">
          {/* Potência do Kit */}
          <div className="form-group">
            <label className="form-label">Potência do Kit</label>
            <input 
              type="text"
              className="form-input"
              placeholder="Digite a potência (ex: 5 ou 5000,50)"
              value={formatPotencia(filters.inverter_nominal_power)}
              onChange={(e) => {
                handlePotenciaInput(e.target.value)
              }}
            />
          </div>

          {/* Marca do Inversor */}
          <div className="form-group">
            <label className="form-label">Marca do Inversor</label>
            <select 
              className="form-select"
              value={filters.inverter_manufacturer}
              onChange={(e) => handleFilterChange('inverter_manufacturer', e.target.value)}
            >
              <option value="">Selecione a marca</option>
              {getFacetOptions('inverter_manufacturer').map((option, index) => (
                <option key={index} value={option.value}>
                  {option.value} ({option.count} opções)
                </option>
              ))}
            </select>
          </div>

          {/* Potência Nominal do Inversor */}
          <div className="form-group">
            <label className="form-label">Potência Nominal do Inversor</label>
            <select 
              className="form-select"
              value={filters.inverter_nominal_power}
              onChange={(e) => handleFilterChange('inverter_nominal_power', e.target.value)}
            >
              <option value="">Selecione a potência nominal</option>
              {getFacetOptions('inverter_nominal_power').map((option, index) => (
                <option key={index} value={option.value}>
                  {option.value} ({option.count} opções)
                </option>
              ))}
            </select>
          </div>

          {/* Tipo de Rede */}
          <div className="form-group">
            <label className="form-label">Tipo de Rede</label>
            <select 
              className="form-select"
              value={filters.network_type}
              onChange={(e) => handleFilterChange('network_type', e.target.value)}
            >
              <option value="">Selecione o tipo de rede</option>
              {getFacetOptions('network_type').map((option, index) => (
                <option key={index} value={option.value}>
                  {option.value} ({option.count} opções)
                </option>
              ))}
            </select>
          </div>

          {/* Estrutura */}
          <div className="form-group">
            <label className="form-label">Estrutura</label>
            <select 
              className="form-select"
              value={filters.structure_installation}
              onChange={(e) => handleFilterChange('structure_installation', e.target.value)}
            >
              <option value="">Selecione a estrutura</option>
              {getFacetOptions('structure_installation').map((option, index) => (
                <option key={index} value={option.value}>
                  {option.value} ({option.count} opções)
                </option>
              ))}
            </select>
          </div>
        </div>
        
        {/* Botão Montar Kit */}
        <div className="kit-actions">
          <button 
            className="montar-kit-button"
            onClick={fetchCustomCart}
            disabled={loadingCart || !canAssembleKit}
          >
            {loadingCart ? '🔄 Montando Kit...' : '🔧 Montar Kit'}
          </button>
        </div>
      </div>

      {/* Visualização do Carrinho */}
      {showCart && cartData && (
        <div className="cart-container">
          <div className="cart-header">
            <h3 className="cart-title">🛒 Kit Montado</h3>
            <button 
              className="close-cart-button"
              onClick={() => setShowCart(false)}
            >
              ✕
            </button>
          </div>
          
          {/* Informações do Kit */}
          <div className="kit-info">
            <div className="kit-basic-info">
              <h4>📋 Informações do Kit</h4>
              <div className="info-grid">
                <div className="info-item">
                  <span className="info-label">ID do Kit:</span>
                  <span className="info-value">{cartData.id}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Região:</span>
                  <span className="info-value">{cartData.region}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Status:</span>
                  <span className="info-value">{cartData.status?.description}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Tipo:</span>
                  <span className="info-value">{cartData.type?.description}</span>
                </div>
              </div>
            </div>
            
            {/* Big Numbers */}
            {cartData.big_numbers && cartData.big_numbers.length > 0 && (
              <div className="big-numbers">
                <h4>📊 Números Principais</h4>
                <div className="big-numbers-grid">
                  {cartData.big_numbers.map((number, index) => (
                    <div key={index} className="big-number-item">
                      <div className="big-number-value">{number.value}</div>
                      <div className="big-number-label">{number.label}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Itens do Kit */}
          {cartData.items && cartData.items.length > 0 && (
            <div className="kit-items">
              <h4>📦 Itens do Kit</h4>
              <div className="items-grid">
                {cartData.items.map((item, index) => (
                  <div key={index} className="item-card">
                    <div className="item-image">
                      {item.images && item.images.length > 0 ? (
                        <img 
                          src={item.images[0].url} 
                          alt={item.description}
                          className="item-img"
                        />
                      ) : (
                        <div className="item-placeholder">📦</div>
                      )}
                    </div>
                    <div className="item-details">
                      <h5 className="item-title">{item.description}</h5>
                      <div className="item-sku">SKU: {item.sku}</div>
                      <div className="item-category">Categoria: {item.category}</div>
                      <div className="item-quantity">Quantidade: {item.amount}</div>
                      <div className="item-price">
                        Preço: R$ {item.price?.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                      </div>
                      <div className="item-total">
                        Total: R$ {item.total?.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                      </div>
                      {item.info && item.info.length > 0 && (
                        <div className="item-info">
                          {item.info.map((info, infoIndex) => (
                            <div key={infoIndex} className="info-detail">
                              <span className="info-detail-label">{info.title}:</span>
                              <span className="info-detail-value">{info.value}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Resumo Financeiro */}
          <div className="cart-summary">
            <h4>💰 Resumo Financeiro</h4>
            <div className="summary-grid">
              <div className="summary-item">
                <span className="summary-label">Subtotal:</span>
                <span className="summary-value">
                  R$ {cartData.subtotal?.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                </span>
              </div>
              <div className="summary-item">
                <span className="summary-label">Total dos Produtos:</span>
                <span className="summary-value">
                  R$ {cartData.product_total?.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                </span>
              </div>
              <div className="summary-item total">
                <span className="summary-label">Total Geral:</span>
                <span className="summary-value">
                  R$ {cartData.total?.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                </span>
              </div>
            </div>
          </div>

          {/* Informações de Entrega */}
          {cartData.shipping && (
            <div className="shipping-info">
              <h4>🚚 Informações de Entrega</h4>
              <div className="shipping-details">
                <div className="shipping-address">
                  <h5>Endereço:</h5>
                  <p>
                    {cartData.shipping.address?.street}, {cartData.shipping.address?.number}<br/>
                    {cartData.shipping.address?.district}<br/>
                    {cartData.shipping.address?.city} - {cartData.shipping.address?.state}<br/>
                    CEP: {cartData.shipping.address?.zipcode}
                  </p>
                </div>
                <div className="shipping-recipient">
                  <h5>Destinatário:</h5>
                  <p>
                    {cartData.shipping.recipient?.name}<br/>
                    {cartData.shipping.recipient?.email}<br/>
                    {cartData.shipping.recipient?.phone}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Loading e Error States */}
      {loading && (
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Carregando opções...</p>
        </div>
      )}

      {error && (
        <div className="error-message">
          ⚠️ Erro ao carregar dados: {error}
          <br />
          <small>Verifique sua conexão e tente novamente.</small>
        </div>
      )}

      {/* Debug Info */}
      {process.env.NODE_ENV === 'development' && (
        <div className="debug-info">
          <h4>Debug Info:</h4>
          <pre>{JSON.stringify({ filters, facets: facets.length, canAssembleKit }, null, 2)}</pre>
        </div>
      )}
    </div>
  )
}

export default MonteSeuKit
